/**
 * Learner license helpers
 *
 * Licenses persist server-side via `LearnerProfileClient` (a bulk
 * replace — see ProfileInfoSection). The editor works on a `LicenseDraft`
 * that mirrors the API `LearnerLicense` plus a stable client `key` so
 * React can track rows that don't have a server id yet. Whether the
 * license sub-section shows at all is driven by the selected
 * profession's `isLicensed` flag, not a hardcoded role list.
 */

import { LearnerLicense, LearnerLicenseItem } from '../apis/api-client';

export interface LicenseDraft {
  /** Stable client key for React lists — unsaved rows have no server id. */
  key: string;
  /** Server id; absent for rows the learner just added. */
  id?: number;
  /** 2-letter USPS code (e.g. 'CA'), or 'OTHER' for non-US. */
  state: string;
  /** Free-text jurisdiction when `state === 'OTHER'`. */
  customStateName?: string;
  licenseNumber: string;
}

let keyCounter = 0;
/** Client-only key for a freshly added (unsaved) license row. */
export function newLicenseKey(): string {
  keyCounter += 1;
  return `lic-${keyCounter}`;
}

/** Map a persisted API license into an editor draft. */
export function apiLicenseToDraft(license: LearnerLicense): LicenseDraft {
  return {
    key: newLicenseKey(),
    id: license.id,
    state: license.state ?? '',
    customStateName: license.customStateName ?? undefined,
    licenseNumber: license.licenseNumber ?? '',
  };
}

/** Map an editor draft into the bulk-save item shape. */
export function draftToLicenseItem(draft: LicenseDraft): LearnerLicenseItem {
  return {
    id: draft.id,
    state: draft.state,
    // Custom jurisdiction is only meaningful for non-US licenses.
    customStateName: draft.state === 'OTHER' ? draft.customStateName : undefined,
    licenseNumber: draft.licenseNumber,
  };
}

/** 50 US states + DC. `'OTHER'` is the escape hatch for non-US licenses. */
export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];
